import type {
  ChangePassInput,
  CreateAccountInput,
  CreateTeacherAccountResponse,
  LoginInput,
  LoginResponse,
  SocialAuthInput,
  SocialAuthProvider,
} from "~/types/auth";
import { createPublicRequestConfig } from "~/repository/authSession";
import HttpFactory from "~/repository/factory";
import { ENDPOINTS } from "~/repository/endpoints";
import { normalizeEmailIdentifier, normalizeEmailInput } from "~/utils/email";

class AuthModule extends HttpFactory {
  async login(credentials: LoginInput): Promise<LoginResponse> {
    const normalizedCredentials = {
      ...credentials,
      username: normalizeEmailIdentifier(credentials.username),
    };

    return await this.post(
      `${ENDPOINTS.AUTH.LOGIN}`,
      normalizedCredentials,
      createPublicRequestConfig(),
    );
  }

  async studentLogin(credentials: LoginInput): Promise<LoginResponse> {
    const normalizedCredentials = {
      ...credentials,
      username: normalizeEmailIdentifier(credentials.username),
    };

    return await this.post(
      `${ENDPOINTS.AUTH.STUDENT_LOGIN}`,
      normalizedCredentials,
      createPublicRequestConfig(),
    );
  }

  async redeemSchoolStudentQuickAccess(token: string): Promise<LoginResponse> {
    return await this.post(
      ENDPOINTS.AUTH.SCHOOL_STUDENT_QUICK_ACCESS,
      { token },
      createPublicRequestConfig(),
    );
  }

  async recordLogin(): Promise<void> {
    return await this.post<void>(`${ENDPOINTS.AUTH.RECORD_LOGIN}`, undefined);
  }

  async socialAuth(
    provider: SocialAuthProvider,
    credentials: SocialAuthInput,
  ): Promise<LoginResponse> {
    return await this.post(
      `${ENDPOINTS.AUTH.SOCIAL_AUTH}${provider}/`,
      credentials,
      createPublicRequestConfig(),
    );
  }

  async studentSocialAuth(
    provider: SocialAuthProvider,
    credentials: SocialAuthInput,
  ): Promise<LoginResponse> {
    return await this.post(
      `${ENDPOINTS.AUTH.STUDENT_SOCIAL_AUTH}${provider}/`,
      credentials,
      createPublicRequestConfig(),
    );
  }

  async changePassword(data: ChangePassInput): Promise<void> {
    return await this.put(`${ENDPOINTS.AUTH.CHANGE_PASSWORD}`, data);
  }

  async createTeacherAccount(
    account: CreateAccountInput,
  ): Promise<CreateTeacherAccountResponse> {
    const normalizedAccount = {
      ...account,
      email: normalizeEmailInput(account.email),
    };

    return await this.post(
      `${ENDPOINTS.AUTH.CREATE_TEACHER_ACCOUNT}`,
      normalizedAccount,
      createPublicRequestConfig(),
    );
  }

  async createStudentAccount(
    account: CreateAccountInput,
  ): Promise<LoginResponse> {
    const normalizedAccount = {
      ...account,
      email: normalizeEmailInput(account.email),
    };

    return await this.post(
      `${ENDPOINTS.AUTH.CREATE_STUDENT_ACCOUNT}`,
      normalizedAccount,
      createPublicRequestConfig(),
    );
  }

  async resetPassword(
    email: string,
    context?: { school_slug?: string; school_invitation_token?: string },
  ): Promise<void> {
    return await this.post(
      `${ENDPOINTS.AUTH.PASSWORD_RESET}`,
      { email: normalizeEmailInput(email), ...context },
      createPublicRequestConfig(),
    );
  }

  async resetPasswordValidateToken(token: string): Promise<void> {
    return await this.post(
      `${ENDPOINTS.AUTH.PASSWORD_RESET_VALIDATE_TOKEN}`,
      {
        token,
      },
      createPublicRequestConfig(),
    );
  }

  async resetPasswordConfirm(token: string, password: string): Promise<void> {
    return await this.post(
      `${ENDPOINTS.AUTH.PASSWORD_RESET_CONFIRMATION}`,
      {
        token,
        password,
      },
      createPublicRequestConfig(),
    );
  }

  async changeStudentPassword(
    studentId: number,
    password: string,
  ): Promise<void> {
    return await this.post(
      `${ENDPOINTS.AUTH.STUDENT_PASSWORD_RESET}${studentId}/`,
      { password },
    );
  }

  async generateStudentLoginToken(
    studentId: number,
  ): Promise<Pick<LoginResponse, "access" | "refresh">> {
    return await this.post(
      `${ENDPOINTS.AUTH.GENERATE_STUDENT_LOGIN_TOKEN}${studentId}/`,
      {},
    );
  }

  async changePasswordFirstTime(password: string): Promise<void> {
    return await this.post(`${ENDPOINTS.AUTH.CHANGE_PASSWORD_FIRST_TIME}`, {
      password,
    });
  }

  async deleteAccount(): Promise<void> {
    return await this.post(`${ENDPOINTS.AUTH.DELETE_ACCOUNT}`, {});
  }

  async impersonate(email: string): Promise<LoginResponse> {
    return await this.post(`${ENDPOINTS.ADMIN.IMPERSONATE}`, {
      email: normalizeEmailInput(email),
    });
  }
}

export default AuthModule;
